import './history-page.scss';

import NiceModal from '@ebay/nice-modal-react';
import { History, Loader2, Search } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useAppDispatch, useAppSelector } from '@/app/store/hooks';
import {
  historyActions,
  selectHistoryHasMore,
  selectHistoryLoading,
  selectRollsHistory,
} from '@/entities/rolls';
import { rollsApi } from '@/entities/rolls';
import { RollsRoundHistory } from '@/entities/rolls';
import { MODALS } from '@/shared/config/modals';
import { formatCurrencyBreakdown, formatUsd } from '@/shared/lib/currency';
import { useTelegramBackButton } from '@/shared/lib/telegram/useTelegramBackButton';
import { PlayerAvatar } from '@/shared/ui/PlayerAvatar';

const showRoundDetail = (roundId: string) => NiceModal.show(MODALS.ROLLS_ROUND_DETAIL, { roundId });

export const HistoryPage = () => {
  useTelegramBackButton();

  const dispatch = useAppDispatch();
  const history = useAppSelector(selectRollsHistory);
  const hasMore = useAppSelector(selectHistoryHasMore);
  const loading = useAppSelector(selectHistoryLoading);

  const [searchValue, setSearchValue] = useState('');
  const [searchResults, setSearchResults] = useState<RollsRoundHistory[] | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const observer = useRef<IntersectionObserver | null>(null);

  const sentinelRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          dispatch(historyActions.fetchNextPage());
        }
      });

      if (node) observer.current.observe(node);
    },
    [loading, hasMore, dispatch],
  );

  useEffect(() => {
    return () => {
      if (observer.current) observer.current.disconnect();
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const handleSearchChange = (value: string) => {
    const v = value.replace(/\D/g, '');
    setSearchValue(v);

    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!v) {
      setSearchResults(null);
      setSearchLoading(false);
      return;
    }

    setSearchLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const result = await rollsApi.getHistory(1, 20, v);
        setSearchResults(result.items);
      } catch {
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 400);
  };

  const isSearching = searchValue.length > 0;
  const displayList = isSearching ? (searchResults ?? []) : history;
  const showSpinner = isSearching ? searchLoading : loading;
  const showEmpty = !showSpinner && displayList.length === 0;

  return (
    <div className="history-page">
      <div className="history-page__header history-page__header--in">
        <History className="history-page__header-icon" />
        <h1 className="history-page__title">Round History</h1>
      </div>

      <div className="history-page__search">
        <Search className="history-page__search-icon" />
        <input
          className="history-page__search-input"
          type="text"
          inputMode="numeric"
          placeholder="Search by round #"
          value={searchValue}
          onChange={(e) => handleSearchChange(e.target.value)}
        />
        {searchLoading && <Loader2 className="history-page__search-spinner" />}
      </div>

      {showSpinner && displayList.length === 0 ? (
        <div className="history-page__list">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="history-page__skeleton">
              <div className="history-page__skeleton-avatar" />
              <div className="history-page__skeleton-text">
                <div className="history-page__skeleton-line history-page__skeleton-line--wide" />
                <div className="history-page__skeleton-line history-page__skeleton-line--narrow" />
              </div>
            </div>
          ))}
        </div>
      ) : displayList.length > 0 ? (
        <div className="history-page__list">
          {displayList.map((round) => (
            <div
              key={round.id}
              className="history-page__item"
              onClick={() => showRoundDetail(round.id)}>
              <div className="history-page__item-left">
                <PlayerAvatar
                  id={round.winnerId}
                  avatar={round.winnerAvatar}
                  color={round.winnerColor}
                  size={32}
                />
                <div>
                  <div className="history-page__item-winner">Round #{round.index}</div>
                  <div className="history-page__item-meta">
                    {round.playerCount} players &middot;
                  </div>
                </div>
              </div>
              <span className="history-page__item-pot">
                Total Pot: {formatCurrencyBreakdown(round.potsByCurrency)} ·{' '}
                {formatUsd(round.totalPot)}
              </span>
            </div>
          ))}

          {!isSearching && hasMore && (
            <div ref={sentinelRef} className="history-page__sentinel">
              {loading && <Loader2 className="history-page__spinner" />}
            </div>
          )}
        </div>
      ) : showEmpty ? (
        <p className="history-page__empty history-page__empty--in">
          {isSearching ? 'No rounds found' : 'No rounds yet'}
        </p>
      ) : null}
    </div>
  );
};
